import {useState, useMemo, useEffect} from 'react';
import {
    Button,
    Form,
    InputGroup,
    Badge,
    ListGroup,
    Stack,
    Spinner,
} from 'react-bootstrap';

interface Usuario {
    rut: string;
    nombre: string;
    correo: string;
    zona: string;
}

interface Props {
    onCreate?: (nombre: string, selectedIds: string[]) => Promise<void>;
    onGetUsers?: () => Promise<any[]>;
}

function initials(nombre: string) {
    return nombre.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

// Función para mapear User a Usuario
function mapUserToUsuario(user: any, index: number): Usuario {
    return {
        rut: user.rut,
        nombre: user.nombre,
        correo: user.correo || '',
        zona: user.zona || 'Sin zona'
    };
}

export function CreateDistributionList({onCreate, onGetUsers}: Props) {
    const [users, setUsers] = useState<Usuario[]>([]);
    const [nombre, setNombre] = useState('');
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [pageLoad, setPageLoad] = useState(false);


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const fetchedUsers = onGetUsers ? await onGetUsers() : [];
                const usuariosConvertidos: Usuario[] = fetchedUsers.map((user, index) =>
                    mapUserToUsuario(user, index)
                );
                setUsers(usuariosConvertidos);
                setPageLoad(true);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        fetchUsers();
    },[]);

    const filtrados = useMemo(() =>
        users.filter((u) =>
            u.nombre.toLowerCase().includes(query.toLowerCase()) ||
            u.rut.toLowerCase().includes(query.toLowerCase()) ||
            u.zona.toLowerCase().includes(query.toLowerCase())
        ), [query,pageLoad]
    );

    const toggleUsuario = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleCrear = async () => {
        if (!nombre.trim() || selected.size === 0) return;
        setLoading(true);
        try {
            if (onCreate) {
                await onCreate(nombre.trim(), Array.from(selected));
                setNombre('');
                setQuery('');
                setSelected(new Set());
            }
            alert("Lista de distribución creada exitosamente");
        } catch (error: any) {
            console.error('Error creating distribution list:', error);
        }finally {
            setLoading(false);
        }
    };

    const canSubmit = nombre.trim().length > 0 && selected.size > 0 && !loading;

    return (
        <div className="w-100 p-4">
            <div className="border rounded-3 p-4 mb-3 bg-white">
                <h2 className="fs-6 fw-medium mb-3">Nueva lista de distribución</h2>

                {/* Nombre */}
                <Form.Group className="mb-3">
                    <Form.Label className="text-secondary small">Nombre de la lista</Form.Label>
                    <Form.Control
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        placeholder="Ej: Supervisores Zona Chillán"
                        size="sm"
                    />
                </Form.Group>

                {/* Búsqueda */}
                <Form.Group className="mb-2">
                    <Form.Label className="text-secondary small">Agregar usuarios</Form.Label>
                    <InputGroup size="sm">
                        <InputGroup.Text className="bg-white">
                            <i className="bi bi-search text-secondary" />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Buscar por nombre, RUT o zona..."
                        />
                    </InputGroup>
                </Form.Group>

                {/* Lista de usuarios */}
                <ListGroup style={{ maxHeight: 224, overflowY: 'auto' }} className="mb-2">
                    {filtrados.length === 0 ? (
                        <ListGroup.Item className="text-center text-secondary small py-4">
                            Sin resultados
                        </ListGroup.Item>
                    ) : (
                        filtrados.map((u) => {
                            const isSelected = selected.has(u.rut);
                            return (
                                <ListGroup.Item
                                    key={u.rut}
                                    action
                                    onClick={() => toggleUsuario(u.rut)}
                                    active={isSelected}
                                    className="d-flex align-items-center gap-2 py-2 px-3"
                                    style={isSelected ? { background: '#EFF6FF', borderColor: '#BFDBFE', color: 'inherit' } : {}}
                                >
                                    {/* Avatar */}
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-medium"
                                        style={{
                                            width: 32, height: 32, fontSize: 11,
                                            background: isSelected ? '#BFDBFE' : '#F3F4F6',
                                            color:      isSelected ? '#1E40AF' : '#6B7280',
                                        }}
                                    >
                                        {initials(u.nombre)}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-grow-1 overflow-hidden">
                                        <p className="mb-0 small fw-medium text-truncate">{u.nombre}</p>
                                        <p className="mb-0 text-secondary" style={{ fontSize: 11 }}>{u.rut} · {u.correo}</p>
                                    </div>

                                    <Badge bg="secondary" className="fw-normal text-truncate" style={{ background: '#F3F4F6 !important' }}>
                                        {u.zona}
                                    </Badge>

                                    {isSelected && (
                                        <span className="text-primary ms-1" style={{ fontSize: 14 }}>✓</span>
                                    )}
                                </ListGroup.Item>
                            );
                        })
                    )}
                </ListGroup>

                {/* Chips seleccionados */}
                {selected.size > 0 && (
                    <Stack direction="horizontal" gap={1} className="flex-wrap mt-2">
                        {Array.from(selected).map((rut) => {
                            const u = users.find((u) => u.rut === rut)!;
                            return (
                                <Badge
                                    key={rut}
                                    className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-normal"
                                    style={{ background: '#DBEAFE', color: '#1E40AF', fontSize: 12 }}
                                >
                                    {u.nombre.split(' ')[0]}
                                    <button
                                        onClick={() => toggleUsuario(rut)}
                                        style={{ background: 'none', border: 'none', color: '#2563EB', lineHeight: 1, padding: 0, cursor: 'pointer', fontSize: 14 }}
                                        aria-label={`Quitar ${u.nombre}`}
                                    >
                                        ×
                                    </button>
                                </Badge>
                            );
                        })}
                    </Stack>
                )}
            </div>

            {/* Footer */}
            <div className="d-flex align-items-center justify-content-between">
            <span className="text-secondary small">
              {selected.size} usuario{selected.size !== 1 ? 's' : ''} seleccionado{selected.size !== 1 ? 's' : ''}
            </span>
                <Stack direction="horizontal" gap={2}>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => { setNombre(''); setQuery(''); setSelected(new Set()); }}
                    >
                        Limpiar
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleCrear}
                        disabled={!canSubmit}
                    >
                        {loading ? (
                            <><Spinner animation="border" size="sm" className="me-1" />Creando...</>
                        ) : (
                            '+ Crear lista'
                        )}
                    </Button>
                </Stack>
            </div>
        </div>
    );
}